import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Gift, Copy, Share2, Users, Wallet, AlertTriangle, Info, Clock } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import SEO from "../components/SEO";

const Referral = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [copied, setCopied] = useState(false);

  const referralCode = user?.referralCode;
  const shareLink = `${window.location.origin}/auth?ref=${referralCode}`;

  const copyToClipboard = () => {
    if (!referralCode) return;
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnWhatsApp = () => {
    if (!referralCode) return;
    const message = `Join THE_ARCHIVE network and unlock exclusive archive tokens. Use my access key: ${referralCode} \nInitialize here: ${shareLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  if (!user) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-6 transition-colors duration-300 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-white text-black'}`}>
        <SEO title="Access Denied | Network" description="Login to initialize referral protocol." />
        <div className={`text-center border-2 p-12 rounded-[3rem] transition-all ${isDarkMode ? 'border-white/5 bg-transparent' : 'border-black/5 bg-transparent shadow-2xl shadow-black/5'}`}>
          <p className="text-[10px] font-black uppercase tracking-[0.5em] text-emerald-500 mb-6 animate-pulse">Security_Block</p>
          <h2 className="text-3xl font-black italic uppercase tracking-tighter">Initialize_Session_To_Earn</h2>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-32 pb-20 px-6 transition-colors duration-300 flex flex-col items-center ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-white text-black'}`}>
      <SEO 
        title="Refer & Earn Tokens | The Archive Network" 
        description="Expand the network and earn exclusive archive tokens."
        url="/refer"
      />

      {/* --- HEADER SECTION --- */}
      <div className="text-center max-w-2xl mb-16">
        <div className="inline-flex p-6 rounded-full bg-emerald-500/10 border-2 border-emerald-500/20 mb-8 relative">
          <div className={`absolute inset-0 blur-3xl rounded-full ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-500/10'}`}></div>
          <Gift size={40} className="text-emerald-500 relative z-10" />
        </div>
        <h1 className="text-5xl lg:text-7xl font-black italic tracking-tighter mb-4 uppercase leading-[0.9]">
          Expand_The <br /> <span className="text-emerald-500">Network</span>
        </h1>
        <p className="opacity-40 text-[10px] font-black uppercase tracking-[0.5em] mt-6">
          System_Rewards: Active_Protocol
        </p>
      </div>

      {/* --- NEW: EXPIRY WARNING PROTOCOL --- */}
      <div className={`w-full max-w-2xl mb-10 border-2 p-6 rounded-[2rem] flex items-center gap-6 transition-all ${isDarkMode ? 'border-amber-500/20 bg-amber-500/5' : 'border-amber-500/10 bg-amber-500/5'}`}>
        <div className="p-4 rounded-2xl bg-amber-500/10 border-2 border-amber-500/20">
          <Clock className="text-amber-500" size={24} />
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-500 mb-1">Asset_Expiry_Notice</p>
          <p className={`text-[11px] font-bold uppercase leading-relaxed opacity-80 ${isDarkMode ? 'text-white' : 'text-black'}`}>
            Tokens are valid for <span className="text-amber-500">365 days</span> since your last acquisition. 
            Initialize an order to reset the network timer and protect your vault.
          </p>
        </div>
      </div>

      {/* --- STATS ROW --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl mb-10">
        <StatCard 
          icon={<Wallet className="text-emerald-500" size={22} />} 
          label="Vault_Credit" 
          value={user?.walletBalance || 0} 
          unit="Tokens" 
        />
        <StatCard 
          icon={<Users className="text-emerald-500" size={22} />} 
          label="Network_Size" 
          value={user?.totalReferrals || 0} 
          unit="Recruits" 
        />
      </div>

      {/* --- REFERRAL CODE BOX --- */}
      <div className={`w-full max-w-2xl border-2 p-10 rounded-[3rem] relative mb-16 transition-all ${isDarkMode ? 'border-white/5 bg-transparent' : 'border-black/5 bg-transparent shadow-xl shadow-black/5'}`}>
        <p className="text-center text-[10px] font-black opacity-40 uppercase tracking-[0.5em] mb-10">Access_Grant_Protocol</p>
        
        <div className={`flex flex-col md:flex-row items-center gap-4 border-2 p-3 rounded-[2rem] mb-8 transition-all ${isDarkMode ? 'border-white/10 bg-white/5' : 'border-black/5 bg-black/5'}`}>
          <div className="flex-1 py-4 px-8">
            <span className="text-3xl font-black tracking-[0.4em] italic uppercase">
              {referralCode || "SYNCING..."}
            </span>
          </div>
          <button 
            onClick={copyToClipboard}
            className={`w-full md:w-auto px-10 py-5 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all active:scale-95 ${
              copied ? 'bg-emerald-500 text-black' : (isDarkMode ? 'bg-white text-black hover:bg-emerald-500 hover:text-white' : 'bg-black text-white hover:bg-emerald-500')
            }`}
          >
            {copied ? 'Copied_Success' : 'Copy_Key'}
          </button>
        </div>

        <button 
          onClick={shareOnWhatsApp}
          className="w-full border-2 border-emerald-500/30 text-emerald-500 py-6 rounded-[2rem] font-black uppercase text-[11px] tracking-[0.4em] flex items-center justify-center gap-4 transition-all hover:bg-emerald-500 hover:text-black hover:border-emerald-500"
        >
          <Share2 size={18} /> Deploy_To_WhatsApp
        </button>
      </div>

      {/* --- EARNINGS LOGIC --- */}
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-4 mb-16">
          <div className={`h-[1px] flex-1 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}></div>
          <h4 className="text-[10px] font-black uppercase tracking-[0.6em] opacity-30">Earnings_Logic</h4>
          <div className={`h-[1px] flex-1 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}></div>
        </div>

        <div className="grid gap-12">
          {[
            { id: "01", title: "Initialize_Link", desc: "Broadcast your unique access key to your elite circle." },
            { id: "02", title: "Recruit_Verified", desc: "New members authenticate via OTP and receive 25 startup tokens." },
            { id: "03", title: "Asset_Rewarded", desc: "You receive 50 vault tokens instantly for every verified acquisition." }
          ].map((step, index) => (
            <div key={index} className="flex gap-8 items-start group">
              <div className={`w-14 h-14 rounded-2xl border-2 flex items-center justify-center text-lg font-black italic text-emerald-500 transition-all group-hover:border-emerald-500 ${isDarkMode ? 'border-white/10 bg-transparent' : 'border-black/10 bg-transparent'}`}>
                {step.id}
              </div>
              <div className="pt-2">
                <p className="text-xs font-black uppercase tracking-[0.2em] mb-2">{step.title}</p>
                <p className="opacity-50 text-[11px] uppercase font-bold leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, unit }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`border-2 p-8 rounded-[2.5rem] flex flex-col items-center transition-all hover:border-emerald-500/50 ${isDarkMode ? 'border-white/5 bg-transparent' : 'border-black/5 bg-transparent shadow-lg shadow-black/5'}`}>
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 border-2 ${isDarkMode ? 'border-white/5 bg-white/5' : 'border-black/5 bg-black/5'}`}>
        {icon}
      </div>
      <p className="text-[10px] uppercase font-black opacity-40 tracking-widest mb-2">{label}</p>
      <h3 className="text-4xl font-black italic leading-none">
        {value} <span className="text-sm text-emerald-500 not-italic uppercase tracking-widest ml-1">{unit}</span>
      </h3>
    </div>
  );
};

export default Referral;